import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useState } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { WixIntegration, WixProfile } from 'api/clients/ExternalIntegrationsClient'
import { WixAuthorSelect } from 'components/BlogMetadata/components/integrations/WixAuthorSelect/WixAuthorSelect'
import { WixMember } from 'api/clients/BlogMetadataRecordsClient'
import Styles from './WixSettingsModal.module.scss'

interface WixSettingsModalProps {
  integration: WixIntegration
}

export const WixSettingsModal = ({ integration }: WixSettingsModalProps) => {
  const { closeModal } = useModals()
  const updateExternalIntegration = useUpdateExternalIntegration()

  const profile = integration.additional_data as WixProfile | undefined
  const availableAuthors: WixMember[] = profile?.wix_members ?? []

  const getInitialAuthorId = (): string | undefined => {
    if (profile?.selected_author_id) {
      const existingAuthor = availableAuthors.find((member) => member.id === profile.selected_author_id)
      if (existingAuthor) return profile.selected_author_id
    }
    if (availableAuthors.length === 1) return availableAuthors[0].id
    return undefined
  }

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(getInitialAuthorId)

  const handleConfirm = async () => {
    if (!selectedAuthorId) {
      showToastMessage('Please select a default author', 'error')
      return
    }

    try {
      await updateExternalIntegration.mutateAsync({
        id: integration.id,
        additional_data: {
          ...profile,
          selected_author_id: selectedAuthorId,
        },
      })

      showToastMessage('Wix settings saved successfully!', 'success')
      closeModal()
    } catch {
      showToastMessage('Failed to save Wix settings. Please try again.', 'error')
    }
  }

  const hasMembers = availableAuthors.length > 0

  return (
    <Modal.Root size="sm" aria-label="Configure Wix Integration">
      <Modal.Header title="Wix Integration Settings" />
      <Modal.Content>
        <div className={Styles.content}>
          <Heading level={4}>Default author for all blog posts</Heading>
          {hasMembers ? (
            <WixAuthorSelect
              members={availableAuthors}
              selectedAuthorId={selectedAuthorId}
              onAuthorSelect={setSelectedAuthorId}
              placeholder="Select a default author"
            />
          ) : (
            <Text variant="secondary" color="var(--dark-60)">
              No Wix members found. Members will be available once they are synced from your Wix site.
            </Text>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleConfirm}
            isDisabled={!selectedAuthorId || !hasMembers || updateExternalIntegration.isLoading}
          >
            Save Changes
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
